import { useAppQuery } from '@/hooks/useAppQuery';
import { ISettingTrainingManagerConfiguration } from '@/types/views/trainingManagerConfiguration';
import { useEffect, useState } from 'react';
import { useLocale } from '../providers/LocaleProvider';
import { Dialog, DialogAction } from './dialogs/Dialog';
import { MenuSelect } from './MenuSelect';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (
    trainingManagerConfigurationTypeId?: string,
    trainingManagerConfigurationTypeName?: string
  ) => void;
  isLoading?: boolean;
  trainingManagerConfigurationTypeId?: string;
  isTrainingManager?: boolean;
  isUnassignLoading?: boolean;
}

export const TrainingManagerPermissionsDialog = ({
  isOpen,
  onClose,
  onAssign,
  isLoading,
  trainingManagerConfigurationTypeId,
  isTrainingManager,
  isUnassignLoading,
}: IProps) => {
  const { translate } = useLocale();
  const [selectedTrainingManagerConfiguration, setSelectedTrainingManagerConfiguration] =
    useState<string>('');

  const { data: trainingManagerConfigurations } = useAppQuery<
    { configurationTypes: ISettingTrainingManagerConfiguration[] },
    Error,
    ISettingTrainingManagerConfiguration[]
  >('/Employee/TrainingManagerConfigurationTypes', {
    select: (data) => data.configurationTypes,
  });

  useEffect(() => {
    if (trainingManagerConfigurationTypeId) {
      setSelectedTrainingManagerConfiguration(trainingManagerConfigurationTypeId);
    } else {
      setSelectedTrainingManagerConfiguration('');
    }
  }, [trainingManagerConfigurationTypeId]);

  const handleOnClose = () => {
    setSelectedTrainingManagerConfiguration(trainingManagerConfigurationTypeId || '');
    onClose();
  };

  const handleAssign = (configurationTypeId: string) => {
    let configurationTypeName = undefined;
    if (configurationTypeId) {
      configurationTypeName = trainingManagerConfigurations?.find(
        (c) => c.trainingManagerConfigurationTypeId === configurationTypeId
      )?.name;
    }

    onAssign(configurationTypeId, configurationTypeName);
    setSelectedTrainingManagerConfiguration(trainingManagerConfigurationTypeId || '');
  };

  return (
    <Dialog
      maxWidth='sm'
      open={isOpen}
      onClose={handleOnClose}
      title={translate('addEditGroup.assignAsTrainingManager')}
      actions={[
        {
          label: translate('common.assign'),
          onClick: () => handleAssign(selectedTrainingManagerConfiguration),
          variant: 'filled' as const,
          disabled: !selectedTrainingManagerConfiguration || isLoading || isUnassignLoading,
          isLoading: isLoading,
        },
        ...(isTrainingManager
          ? ([
              {
                label: translate('common.unassign'),
                onClick: () => handleAssign(''),
                variant: 'filled' as const,
                disabled: !selectedTrainingManagerConfiguration || isLoading || isUnassignLoading,
                isLoading: isUnassignLoading,
                acceptDanger: true,
              },
            ] as DialogAction[])
          : []),
        {
          label: translate('common.cancel'),
          onClick: handleOnClose,
          disabled: isLoading || isUnassignLoading,
        },
      ]}
    >
      <MenuSelect
        label={translate('addEditGroup.selectPermission')}
        value={selectedTrainingManagerConfiguration}
        onChange={(e) => {
          setSelectedTrainingManagerConfiguration(e.target.value);
        }}
        items={trainingManagerConfigurations?.map((c) => ({
          value: c.trainingManagerConfigurationTypeId,
          label: c.name,
        }))}
        sx={{
          width: '100%',
        }}
        clearable={false}
      />
    </Dialog>
  );
};
